import * as React from 'react';
import { Modal } from 'react-bootstrap';
import { Typography, Button, Icon } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import colors from '@packages/core/styles/colors';

export type ConfirmModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onCancel?: () => void;

    title?: React.ReactNode;
    description?: React.ReactNode;
    note?: React.ReactNode;

    confirmLabel?: string;
    cancelLabel?: string;

    destructive?: boolean;
    loading?: boolean;
    disabled?: boolean;

    closeOnBackdrop?: boolean;
    closeOnEsc?: boolean;
    hideCancel?: boolean;

    size?: 'sm' | 'md' | 'lg' | 'xl';

    className?: string;
    dialogClassName?: string;
    headerClassName?: string;
    bodyClassName?: string;
    footerClassName?: string;

    headerExtra?: React.ReactNode;
    footerExtra?: React.ReactNode;
    icon?: React.ReactNode;
    children?: React.ReactNode;

    'data-testid'?: string;
};

const useStyles = createUseStyles({
    modal: {
        height: '248px',
    },
    modalLayer: {
        zIndex: 1065,
    },
    backdropLayer: {
        zIndex: 1060,
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        padding: '16px 24px',
    },
    bodyContent: {
        padding: '16px 24px',
    },

    footer: {},

    footerExtra: {
        marginLeft: 'auto',
    },
});

const styles = StyleSheet.create({
    title: {
        fontWeight: '600',
        fontSize: 21,
        color: colors.textDarkPrimary,
    },
    description: {
        fontSize: 15,
        fontWeight: '400',
        color: colors.textDarkPrimary,
    },
    confirmButtonSpacing: {
        marginLeft: 12,
    },
});

export const ConfirmModal = React.memo(function ConfirmModal(props: ConfirmModalProps) {
    const {
        open,
        onClose,
        onConfirm,
        onCancel,

        title = 'Confirm action',
        description,
        note,

        confirmLabel = 'Confirm',
        cancelLabel = 'Cancel',

        destructive = false,
        loading = false,
        disabled = false,

        closeOnBackdrop = true,
        closeOnEsc = true,
        hideCancel = false,

        size = 'md',
        className,
        dialogClassName,
        headerClassName,
        bodyClassName,
        footerClassName,

        headerExtra,
        footerExtra,
        icon,
        children,

        'data-testid': dataTestId,
    } = props;

    const titleId = React.useId();
    const descId = React.useId();

    const rbSize: 'sm' | 'lg' | 'xl' | undefined = size === 'md' ? undefined : (size as 'sm' | 'lg' | 'xl');

    const backdrop: true | 'static' = closeOnBackdrop ? true : 'static';

    const confirmVariant = destructive ? 'danger' : 'primary';
    const cancelVariant = 'ghost-gray';

    const ariaDescribedBy = children || description || note ? descId : undefined;

    const classes = useStyles();

    return (
        <Modal
            show={open}
            onHide={onClose}
            centered
            size={rbSize}
            backdropClassName={classes.backdropLayer}
            className={`${className ?? ''} ${classes.modalLayer}`}
            dialogClassName={`${dialogClassName ?? ''} ${classes.modal}`}
            aria-labelledby={titleId}
            aria-describedby={ariaDescribedBy}
            data-testid={dataTestId ?? 'confirm-dialog'}
            backdrop={backdrop}
            keyboard={closeOnEsc}
        >
            <Modal.Header className={`${classes.headerContent} ${headerClassName}`}>
                {icon === undefined ? null : (
                    <span aria-hidden>{typeof icon === 'string' ? <Icon name={icon} /> : icon}</span>
                )}
                <Typography id={titleId} style={styles.title}>
                    {title}
                </Typography>
                {headerExtra ? <div>{headerExtra}</div> : null}
            </Modal.Header>

            <Modal.Body className={`${classes.bodyContent} ${bodyClassName}`}>
                <div className="d-flex flex-column">
                    {children ? (
                        <div id={descId}>{children}</div>
                    ) : (
                        <>
                            <Typography id={descId} variant="h5" style={styles.description}>
                                {description ?? 'Are you sure you want to proceed?'}
                            </Typography>

                            {note && (
                                <Typography variant="lead" style={styles.description}>
                                    {note}
                                </Typography>
                            )}
                        </>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className={footerClassName}>
                {!hideCancel && (
                    <Button
                        title={cancelLabel}
                        variant={cancelVariant}
                        onPress={onCancel ?? onClose}
                        disabled={loading}
                        data-testid="confirm-dialog-cancel"
                    />
                )}

                <Button
                    title={confirmLabel}
                    variant={confirmVariant}
                    style={styles.confirmButtonSpacing}
                    onPress={onConfirm}
                    disabled={disabled || loading}
                    isLoading={loading}
                    data-testid="confirm-dialog-confirm"
                />

                {footerExtra ? <div className={classes.footerExtra}>{footerExtra}</div> : null}
            </Modal.Footer>
        </Modal>
    );
});
